
import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import ProtectedRoute from "@/components/layout/ProtectedRoute";

// Lazy load all page components to reduce initial bundle size
const Index = React.lazy(() => import("./pages/Index"));
const ParcelsPage = React.lazy(() => import("./pages/ParcelsPage"));
const NewParcelPage = React.lazy(() => import("./pages/parcels/NewParcelPage"));
const ParcelMapPage = React.lazy(() => import("./pages/parcels/ParcelMapPage"));
const ParcelDetailPage = React.lazy(() => import("./pages/parcels/ParcelDetailPage"));
const ObservationsPage = React.lazy(() => import("./pages/ObservationsPage"));
const NewObservationPage = React.lazy(() => import("./pages/observations/NewObservationPage"));
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const AddPage = React.lazy(() => import("./pages/AddPage"));
const AuthPage = React.lazy(() => import("./pages/AuthPage"));
const OnboardingPage = React.lazy(() => import("./pages/OnboardingPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const ObservationDetailPage = React.lazy(() => import("./pages/observations/ObservationDetailPage"));
const ActivitiesPage = React.lazy(() => import("./pages/ActivitiesPage"));
const NewActivityPage = React.lazy(() => import("./pages/activities/NewActivityPage"));
const ActivityDetailPage = React.lazy(() => import("./pages/activities/ActivityDetailPage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const PricingPage = React.lazy(() => import("./pages/PricingPage"));

// Create the QueryClient outside the component
const queryClient = new QueryClient();

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-farm-dark-green dark:border-farm-light-green"></div>
  </div>
);

const App = () => {
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <NotificationProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<OnboardingPage />} />
                      <Route path="/auth" element={<AuthPage />} />
                      
                      {/* Protected Routes */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="/dashboard" element={<Index />} />
                        
                        {/* Parcels Routes */}
                        <Route path="/parcels" element={<ParcelsPage />} />
                        <Route path="/parcels/new" element={<NewParcelPage />} />
                        <Route path="/parcels/map" element={<ParcelMapPage />} />
                        <Route path="/parcels/:id" element={<ParcelDetailPage />} />
                        
                        {/* Observations Routes */}
                        <Route path="/observations" element={<ObservationsPage />} />
                        <Route path="/observations/new" element={<NewObservationPage />} />
                        <Route path="/observations/:id" element={<ObservationDetailPage />} />
                        
                        {/* Activities Routes */}
                        <Route path="/activities" element={<ActivitiesPage />} />
                        <Route path="/activities/new" element={<NewActivityPage />} />
                        <Route path="/activities/:id" element={<ActivityDetailPage />} />
                        
                        {/* Profile Route */}
                        <Route path="/profile" element={<ProfilePage />} />
                        
                        {/* Information Routes */}
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        
                        <Route path="/add" element={<AddPage />} />
                      </Route>
                      
                      {/* Catch-all Route */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </TooltipProvider>
              </NotificationProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

export default App;
